<script lang="ts">
	import type { LocalizedString } from "@core/schema/Locale.js";
	import { companyValuesId } from "@core/schema/ids/companyValuesId.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import Formatted from "./common/Formatted.svelte";
	import Link from "./common/Link.svelte";
	import Media from "./common/Media.svelte";
	import StickySection from "./common/StickySection.svelte";
	import TextWithIcon from "./common/TextWithIcon.svelte";
	import { ButtonVariant } from "./common/getButtonClass.js";

	const { tenant } = getGlobalTemplateProps();

	const {
		image,
		mobileImage,
		alt = "Hero image",
		heading,
		text,
		text2,
		text3,
		text4,
		buttonText,
		vertical,
		customIdHref = companyValuesId,
		brightness = 140,
		objectPosition = "center",
	}: {
		image: string;
		mobileImage?: string;
		alt?: string;
		heading?: string;
		text?: string;
		text2?: string;
		text3?: string;
		text4?: string;
		buttonText?: string;
		vertical?: boolean;
		customIdHref?: LocalizedString;
		brightness?: number;
		objectPosition?: string;
	} = $props();
</script>

<section
	class="container-whole h-[20rem] sm:h-[22rem] md:h-[24.25rem] max-h-[60vw] min-h-[12rem] rounded-2xl overflow-hidden relative"
	style="--hero-brightness: {brightness}%; --hero-object-position: {objectPosition};"
>
	<Media
		class="rounded-2xl object-cover w-full h-full opacity-90 shadow-lg [filter:grayscale(100%)_brightness(var(--hero-brightness))] [object-position:var(--hero-object-position)]"
		source={{ src: image, type: "image/webp" }}
		sourcesets={mobileImage ? { "<md": mobileImage } : {}}
		width={1440}
		height={400}
		{alt}
		eager
		preload={{ fetchpriority: "high" }}
	/>
	<div class="absolute inset-0 bg-zms-blue/80 rounded-2xl mix-blend-multiply"></div>
	<div
		class="absolute inset-0 bg-gradient-to-r opacity-30 from-zms-blue/90 via-zms-blue/80 to-zms-blue/60 rounded-2xl"
	></div>
</section>
<section class="full-width grid-container py-6 sm:py-8 md:py-12">
	<div class="grid gap-6 md:gap-8 {vertical ? 'md:flex flex-col items-center' : 'lg:grid-cols-2 lg:gap-20'}">
		{#if vertical}
			<h1 class="text-zms-blue md:text-center font-light tracking-[-4%] md:w-2/3">
				<Formatted text={heading} />
			</h1>
		{:else}
			<StickySection>
				<h1
					class="text-zms-blue font-light tracking-[-4%] text-left text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl"
				>
					<Formatted text={heading} />
				</h1>
			</StickySection>
		{/if}
		<div class="flex flex-col gap-4 sm:gap-6 md:gap-8 lg:gap-16 md:mt-1 h-full">
			<p
				class="font-normal text-sm sm:text-base md:text-lg/[1.4] text-optical-black flex flex-col gap-3 sm:gap-4 md:gap-6 h-full {vertical
					? 'md:text-center'
					: 'text-left'}"
			>
				<span><Formatted {text} /></span>
				{#if text2}
					<span><Formatted text={text2} /></span>
				{/if}
				{#if text3}
					<span><Formatted text={text3} /></span>
				{/if}
				{#if text4}
					<span><Formatted text={text4} /></span>
				{/if}
			</p>
			{#if buttonText}
				<div class="mt-3 {vertical ? 'mx-auto' : ''}">
					<Link href="#{customIdHref[tenant.locale]}" variant={ButtonVariant.OutlinedWithIcon}>
						<TextWithIcon>
							{#if buttonText}
								<Formatted text={buttonText} />
							{/if}
						</TextWithIcon>
					</Link>
				</div>
			{/if}
		</div>
	</div>
</section>
